import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '../lib/utils'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { Card, CardBody, CardHeader } from './ui/Card'
import { DownloadCsvButton } from './ui/DownloadCsvButton'
import { joinList, toCsv, type CsvColumn } from '../lib/csv'
import { downloadCsv } from '../lib/download'
import type { DeadEnd, DeadEndFinding, DeadEndPage } from '../api/types'

/**
 * What each supersession reason means, in the words a reader needs at the row.
 *
 * Keyed by the API's `superseded_by` vocabulary, which lives in
 * `store.SUPERSESSION_REASONS`. An unrecognised value falls back to a generic
 * sentence rather than rendering no badge — the reason is version skew to show, and
 * a missing badge would put the row back among the real drops.
 */
const SUPERSEDED_TITLES: Record<string, string> = {
  ranked:
    'This hypothesis has a ranked row in this run: either a later review iteration reversed the drop, or an incomplete one competed carrying its gaps.',
  refined:
    'An evolved child of this hypothesis ranked in its place. Evolution is non-destructive, so the parent keeps its recorded drop.',
  absorbed:
    'Folded into a duplicate that ranked. Same target, one board row.',
}

/**
 * What review ruled on and the ranking does not show.
 *
 * A sweep that reviewed 29 hypotheses and ranked 10 looked, from this UI, like a
 * sweep that had 10 ideas — the other 19 and every reason behind them lived in
 * the LangGraph store, which the API cannot read. They are now mirrored into
 * Aurora, and this is where they surface.
 *
 * **Grouped by verdict, not listed under one heading.** The three verdicts are
 * three different results and the reader's next action differs for each: a
 * `failed` row is a finding to keep, an `incomplete` row is a measurement to go
 * and take, an `unreviewed` row is an outage to investigate. One "disqualified"
 * heading over all of them asserted the first about all three.
 *
 * Collapsed by default: it is the run's working, not its result. The count and the
 * per-verdict summary stay visible while collapsed, because a reader has to be able
 * to see that there *is* working without opening it.
 *
 * ── The distinction the whole file turns on ──────────────────────────────────────
 *
 * **`loaded === false` is not `total === 0`.** Three states, three sentences, and
 * they may never collapse: "nothing was ruled out" is a *result* about the run,
 * "could not load" is a fact about this page's fetch, and a run predating
 * `infra/sql/005_dead_ends.sql` has no rows and never will. The counts are also the
 * API's own whole-run totals and are **not** recounted from the page — `dead_ends` is
 * one capped page, so a client-side count silently under-reports every run bigger
 * than it.
 */
export function DeadEndTable({
  deadEnds,
  total,
  counts,
  final,
  loaded,
}: {
  deadEnds: DeadEnd[]
  total: number
  /**
   * Per-verdict totals for the whole run, from the API. Empty when the runtime
   * predates the field — the fallback counts the page and says so, because a
   * silent client-side count under-reports every run bigger than one page.
   */
  counts: Record<string, number>
  /**
   * The same totals with superseded rows removed, and how they were arrived at.
   * Optional: a runtime predating it sends nothing, and the recorded counts are then
   * the honest fallback rather than a computed guess.
   */
  final?: DeadEndPage['final']
  /** False while the fetch is outstanding, or if it failed. See the empty state. */
  loaded: boolean
}) {
  const [open, setOpen] = useState(false)

  // The counts a scientist means by "what did this sweep rule out". `null` when the
  // API could not read the ranking, and the recorded totals are shown instead —
  // zeros there would report an outage as a run that ruled nothing out.
  const finalCounts = final?.counts ?? null
  const superseded = finalCounts ? (final?.superseded ?? 0) : 0

  const groups = GROUPS.map((g) => {
    const rows = deadEnds.filter((d) => d.verdict === g.verdict)
    const recorded = counts[g.verdict] ?? rows.length
    return {
      ...g,
      rows,
      count: finalCounts ? (finalCounts[g.verdict] ?? 0) : recorded,
      recorded,
    }
  })

  // Anything the API produced that this file does not know about. Rendered under
  // its own heading rather than dropped: the verdict column is unconstrained text
  // whose vocabulary lives in Python, so an unrecognised value is version skew to
  // show, not a row to hide. Without this a new verdict would silently vanish
  // from a table whose whole job is to account for every hypothesis.
  const known = new Set(GROUPS.map((g) => g.verdict))
  const unknown = deadEnds.filter((d) => !known.has(d.verdict))

  const summary = groups.filter((g) => g.count > 0)

  const download = () => {
    // Named after the run, read off the rows so it cannot name a run other than
    // the one exported — `TargetTable`'s reasoning.
    downloadCsv(
      `dead-ends-${deadEnds[0]?.run_id ?? 'export'}.csv`,
      toCsv(csvRows(deadEnds), CSV_COLUMNS),
    )
  }

  return (
    <Card>
      <CardHeader
        // Names the level of record, which is the thing a reader most often gets
        // wrong here: these are review outcomes, not a second ranking.
        eyebrow="Not ranked"
        title={
          <span className="flex items-baseline gap-2">
            What review ruled on
            {loaded && (
              <span className="font-mono text-2xs tabular-nums text-slate-500">
                {total}
              </span>
            )}
          </span>
        }
        description={
          loaded ? (
            total === 0 ? (
              'Nothing was ruled out, flagged or left unmeasured in this run.'
            ) : (
              <>
                {summary.map((g, i) => (
                  <span key={g.verdict}>
                    {i > 0 && ', '}
                    <span className="font-mono tabular-nums text-slate-300">
                      {g.count}
                    </span>{' '}
                    {g.summaryLabel}
                  </span>
                ))}
                {unknown.length > 0 && `, ${unknown.length} unrecognised`}
                {'. Whole-run totals from the API, not a count of the rows below.'}
                {/* The 51-rows-for-17-hypotheses case, stated rather than left for a
                    reader to discover by cross-referencing the ranking. Review
                    re-runs across iterations and evolution keeps the parent, so a
                    recorded drop can belong to a hypothesis that is on the board. */}
                {superseded > 0 && (
                  <>
                    {' '}
                    <span className="text-slate-400">
                      {superseded} further{' '}
                      {superseded === 1 ? 'row was' : 'rows were'} superseded by this
                      run&apos;s own ranking and are not counted above.
                    </span>
                  </>
                )}
                {final?.source === 'store_unreachable' && (
                  <>
                    {' '}
                    <span className="text-amber-500/80">
                      The ranking could not be read, so these are the rows recorded,
                      not the ones this run finally ruled out.
                    </span>
                  </>
                )}
              </>
            )
          ) : (
            // Not "0" and not "nothing was ruled out". The fetch has not answered.
            'Waiting on the review ledger for this run.'
          )
        }
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      />

      {open && (
        <CardBody className="space-y-5">
          {/* Three different empty states, deliberately. "Nothing was ruled out"
              is a result about the run; "not recorded" is a fact about the
              harness that produced it — runs from before
              infra/sql/005_dead_ends.sql have no rows and never will. Showing
              either as the other misrepresents the science. */}
          {!loaded ? (
            <p className="text-sm text-slate-500">
              Could not load what this run ruled out. That is not the same as
              nothing having been ruled out.
            </p>
          ) : total === 0 ? (
            <p className="text-sm text-slate-400">
              No hypotheses were ruled out, flagged or left unmeasured in this run.
              Runs recorded before this was tracked show the same thing and mean
              something else — check whether the run predates it before reading
              this as a clean sweep.
            </p>
          ) : (
            <>
              {/* No per-row selection here, unlike the ranked and board tables.
                  Those export a ranking a reader curates; this is the run's
                  working, split across three sub-tables, and a selection spanning
                  them would need a shared set whose count could disagree with any
                  one group's rows. `selectedCount={0}` is therefore not a
                  placeholder — it is what makes the label read "all N on this
                  page", which is exactly what the export holds. */}
              <div className="flex items-center justify-end">
                <DownloadCsvButton
                  selectedCount={0}
                  rowCount={deadEnds.length}
                  noun="rows"
                  onDownload={download}
                />
              </div>

              {groups
                .filter((g) => g.count > 0 || g.rows.length > 0)
                .map((g) => (
                  <VerdictGroup key={g.verdict} group={g} />
                ))}

              {unknown.length > 0 && (
                <VerdictGroup
                  group={{
                    verdict: 'unrecognised',
                    heading: 'Unrecognised verdict',
                    summaryLabel: 'unrecognised',
                    blurb:
                      'This UI does not know these verdicts. They are shown rather than dropped — the vocabulary lives in the harness, so this is most likely a runtime newer than the page.',
                    variant: 'default',
                    rows: unknown,
                    // Both from the page, and equal on purpose: the API sends no
                    // whole-run total for a verdict it does not know about, so there
                    // is nothing to be superseded *against*. Showing a second number
                    // here would invent one.
                    count: unknown.length,
                    recorded: unknown.length,
                  }}
                />
              )}

              {/* No silent caps: if the API's page did not cover the run, say so
                  rather than let the visible rows read as the whole list. The
                  rows are ordered by verdict, so a truncated page drops whole
                  groups off the end — which is why each group states its own
                  run-wide count above. */}
              {deadEnds.length < total && (
                <p className="text-xs text-slate-500">
                  Showing {deadEnds.length} of {total}.
                </p>
              )}
            </>
          )}
        </CardBody>
      )}
    </Card>
  )
}

/** One verdict's rows, under a heading that says what the verdict means. */
function VerdictGroup({
  group,
}: {
  group: Group & { rows: DeadEnd[]; count: number; recorded: number }
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <Badge variant={group.variant}>{group.verdict}</Badge>
        <h4 className="font-display text-sm font-semibold text-slate-200">
          {group.heading}
        </h4>
        <span
          className="text-xs text-slate-500"
          title="The API's whole-run total for this verdict, superseded rows excluded. The rows below are one page of it."
        >
          <span className="font-mono tabular-nums">{group.count}</span> in this run
        </span>
        {/* Both numbers, never one: the difference IS the finding, and a reader who
            saw only the final count would think the log had fewer rows than it has. */}
        {group.recorded > group.count && (
          <span
            className="text-2xs text-slate-600"
            title="Rows recorded for this verdict, including ones a later iteration, an evolved child or a duplicate collapse superseded."
          >
            <span className="font-mono tabular-nums">{group.recorded}</span> recorded
          </span>
        )}
      </div>
      <p className="mb-2 mt-1 max-w-3xl text-xs leading-relaxed text-slate-500">
        {group.blurb}
      </p>

      {group.rows.length === 0 ? (
        // The run has rows for this verdict but none reached this page. Saying so
        // is the difference between "none of these" and "not here".
        <p className="text-xs text-slate-500">Not on this page.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                {['Protein', 'Mechanism', 'Axis', 'Why'].map((h) => (
                  <th
                    key={h}
                    className={cn(
                      'px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500',
                      h === 'Axis' && 'w-28',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.rows.map((d) => (
                <tr
                  key={d.hypothesis_id}
                  className="border-b border-slate-800/70 align-top last:border-b-0"
                >
                  <td className="px-3 py-2">
                    <div className="text-slate-200">{d.protein ?? '—'}</div>
                    <div className="font-mono text-2xs text-slate-500">
                      {d.hypothesis_id}
                      {d.iteration != null && ` · iter ${d.iteration}`}
                    </div>
                    {/* The row is kept and labelled, never hidden. `dead_ends` is
                        append-only and this row is a true record of what review
                        concluded at the time — the badge says it is no longer the
                        run's answer, which is a different claim from it never
                        having been made. */}
                    {d.superseded_by && (
                      <span
                        className="mt-1 inline-block text-2xs text-blue-400"
                        title={SUPERSEDED_TITLES[d.superseded_by] ?? 'This drop is not the run’s final answer about the hypothesis.'}
                      >
                        superseded · {d.superseded_by}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    {d.mechanism ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">
                    {d.axis ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    <ul className="space-y-1">
                      {(d.reasons ?? []).map((r, i) => (
                        <li key={`r${i}`}>{r}</li>
                      ))}
                      {(d.gaps ?? []).map((g, i) => (
                        <li key={`g${i}`} className="text-slate-500">
                          unmeasured: {g}
                        </li>
                      ))}
                      {!d.reasons?.length && !d.gaps?.length && (
                        <li className="text-slate-500">No reason recorded.</li>
                      )}
                    </ul>

                    {/* The field the axis judged, and the measurement that would
                        change the verdict. Both were computed and persisted well
                        before anything read them back, so a reader asking "why did
                        this drop?" had only the rendered bullets above — which are
                        prose about the reasons, not the reasons. `unstated` is
                        shown rather than hidden: it means the FAIL named no field,
                        which is a fact about the review and the strongest signal
                        that the drop is worth re-checking. */}
                    {d.failed_check && (
                      <p className="mt-1 font-mono text-2xs text-slate-400">
                        {d.failed_check}
                      </p>
                    )}
                    {d.would_settle_it && (
                      <p className="mt-1 text-xs text-slate-500">
                        would settle it: {d.would_settle_it}
                      </p>
                    )}
                    {/* Only when it is not `json`, the same rule the review line in
                        the logs follows: a legible verdict needs no annotation, and
                        annotating every row would bury the ones where the harness,
                        not the hypothesis, is what failed. */}
                    {d.verdict_source && d.verdict_source !== 'json' && (
                      <p
                        className="mt-1 text-xs text-amber-500/80"
                        title="Not every part of the reviewer's reply parsed, so this verdict is not citable as written — the drop may be the harness's own failure rather than a finding about the hypothesis."
                      >
                        verdict only partly legible ({d.verdict_source})
                      </p>
                    )}

                    <Findings deadEnd={d} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * The per-axis findings behind a drop, which the CSV has always carried and the
 * screen never did.
 *
 * A drop can fail two axes with different fields and different evidence, and the
 * `reasons` bullets above are *rendered prose about* those values rather than the
 * values — so a reader checking whether a disqualifier is citable had nothing to
 * check. Each finding names its disqualifier, the field it could not support and the
 * evidence, per finding rather than joined.
 *
 * The absence is only remarked on for a `failed` row. `[]` means not recorded — a
 * narrated `append_dead_end` record or a pre-016 row — and for a disqualification
 * that is worth saying, because the drop is then not citable to a field. For
 * `incomplete` and `unreviewed` there is legitimately nothing per-axis to record, and
 * a note on every one of those rows would bury the case that matters.
 */
function Findings({ deadEnd: d }: { deadEnd: DeadEnd }) {
  const findings = d.findings ?? []

  if (findings.length === 0) {
    if (d.verdict !== 'failed') return null
    return (
      <p
        className="mt-1 text-xs text-slate-500"
        title="Structured findings arrive with infra/sql/016_review_provenance.sql, which backfilled nothing. A narrated dead-end record has none either."
      >
        No per-axis findings recorded, so this disqualification cannot be traced to a
        named field.
      </p>
    )
  }

  return (
    <dl className="mt-2 space-y-1.5 border-l border-slate-800 pl-3">
      {findings.map((f, i) => (
        <div key={i} className="text-xs">
          <dt className="flex flex-wrap items-baseline gap-1.5">
            <span className="font-mono text-slate-300">{f.axis ?? 'axis unstated'}</span>
            {f.disqualifier && (
              <Badge
                variant="red"
                className="px-1.5 py-0 text-2xs"
                title="The supported disqualifier that fired, from context/target-criteria."
              >
                {f.disqualifier}
              </Badge>
            )}
            {f.verdict_source && f.verdict_source !== 'json' && (
              <span className="font-mono text-2xs text-amber-500/80">
                {f.verdict_source}
              </span>
            )}
          </dt>
          <dd className="text-slate-500">
            {/* `unstated` is rendered as itself: it means the reviewer's FAIL named
                no field, which was 100% of drops by construction until migration
                016, and it is the strongest signal a drop is worth re-checking. */}
            <span className="font-mono">{f.failed_check ?? 'no field recorded'}</span>
            {f.evidence && <span className="ml-1">— {f.evidence}</span>}
            {f.would_settle_it && (
              <span className="mt-0.5 block">
                would settle it: {f.would_settle_it}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * One CSV record: a dead end paired with one of its findings.
 *
 * **One row per finding, not per hypothesis.** A drop can fail two axes, and the
 * two carry different fields and different evidence — flattening them into one row
 * would either join two `failed_check` values into a cell nothing can filter on, or
 * keep the first and silently discard the second. This is what
 * `016_review_provenance.sql` means by storing `findings` structured "because a CSV
 * export needs each value in its own column".
 *
 * A hypothesis with no findings still gets exactly one row with the finding columns
 * empty — `incomplete` and `unreviewed` legitimately have none, as does any pre-016
 * row, and dropping them would make a table whose whole job is to account for every
 * hypothesis export fewer than it displays.
 */
interface CsvRow {
  d: DeadEnd
  f: DeadEndFinding | null
}

function csvRows(deadEnds: readonly DeadEnd[]): CsvRow[] {
  return deadEnds.flatMap((d): CsvRow[] => {
    const findings = d.findings ?? []
    return findings.length > 0
      ? findings.map((f) => ({ d, f }))
      : [{ d, f: null }]
  })
}

/**
 * The export's columns.
 *
 * The per-finding fields come off the finding and fall back to the row's own
 * summary, which is the joined form the API also carries — so a pre-016 row still
 * exports what it has rather than a blank beside a populated `reasons`.
 *
 * `reasons` and `gaps` are the *rendered bullets* the table shows, kept beside the
 * structured fields rather than instead of them: they are what a reader recognises
 * from the screen, and the fields are what a reader can sort and filter on.
 */
const CSV_COLUMNS: readonly CsvColumn<CsvRow>[] = [
  { header: 'run_id', value: ({ d }) => d.run_id },
  { header: 'hypothesis_id', value: ({ d }) => d.hypothesis_id },
  { header: 'verdict', value: ({ d }) => d.verdict },
  { header: 'protein', value: ({ d }) => d.protein },
  { header: 'mechanism', value: ({ d }) => d.mechanism },
  { header: 'iteration', value: ({ d }) => d.iteration },
  { header: 'axis', value: ({ d, f }) => f?.axis ?? d.axis },
  { header: 'disqualifier', value: ({ f }) => f?.disqualifier },
  {
    header: 'failed_check',
    value: ({ d, f }) => f?.failed_check ?? d.failed_check,
  },
  { header: 'evidence', value: ({ f }) => f?.evidence },
  {
    header: 'would_settle_it',
    value: ({ d, f }) => f?.would_settle_it ?? d.would_settle_it,
  },
  // Not decoration: anything but `json` means the drop may be the harness's own
  // failure to read a reply rather than a finding about the hypothesis, and this
  // column is what a reader sorts on to find the ones worth a recap.
  {
    header: 'verdict_source',
    value: ({ d, f }) => f?.verdict_source ?? d.verdict_source,
  },
  { header: 'source', value: ({ d }) => d.source },
  // Both columns, for rule 2's reason: an empty `superseded_by` beside
  // `supersession_source=store_unreachable` is "could not tell", and beside
  // `ranking` it is "this drop stood". A spreadsheet filtering on one alone would
  // read an outage as a confirmed drop.
  { header: 'superseded_by', value: ({ d }) => d.superseded_by },
  {
    header: 'supersession_source',
    value: ({ d }) => d.supersession_source,
  },
  { header: 'reasons', value: ({ d }) => joinList(d.reasons) },
  { header: 'gaps', value: ({ d }) => joinList(d.gaps) },
  { header: 'created_at', value: ({ d }) => d.created_at },
]

interface Group {
  verdict: string
  heading: string
  /** Wording for the collapsed one-line summary, e.g. "3 disqualified". */
  summaryLabel: string
  blurb: string
  variant: 'red' | 'yellow' | 'blue' | 'default'
}

/**
 * The three verdicts, in the order the API returns them — see
 * `store.DEAD_END_VERDICTS`, which orders the query so each group's rows are
 * contiguous across pages.
 *
 * Each heading is a claim about what the reader is looking at, which is why they
 * are not interchangeable: "Disqualified" is a finding, "Carried forward with
 * gaps" is work outstanding, and "Not falsified" is an admission that the harness
 * failed rather than the hypothesis.
 */
const GROUPS: Group[] = [
  {
    verdict: 'failed',
    heading: 'Disqualified',
    summaryLabel: 'disqualified',
    blurb:
      'A supported disqualifier fired, and the hypothesis was excluded from the tournament. This is a result worth keeping — a rediscovered commercial mode of action is a negative finding, not a blank.',
    variant: 'red',
  },
  {
    verdict: 'incomplete',
    heading: 'Carried forward with gaps',
    summaryLabel: 'carried forward with gaps',
    blurb:
      'Too few decidable axes reached a verdict, so it competed carrying its gaps and may also appear in the ranking above. That is not a contradiction: filtering it would make an unproven hypothesis indistinguishable from a barren run. The gaps are the measurements to go and take.',
    variant: 'yellow',
  },
  {
    verdict: 'unreviewed',
    heading: 'Not falsified',
    summaryLabel: 'not falsified',
    // Blue, not yellow: the point of separating this from `incomplete` is that it
    // is not an evidence gap. Sharing a colour with one would undo in the styling
    // what the split exists to say.
    blurb:
      'The harness tried twice and could not measure. This is an infrastructure failure, not an evidence gap — it says nothing about the hypothesis, and reading it as a weak result would dress an outage as science. Admitted flagged, because failing open loudly beats deleting real work on a transient error.',
    variant: 'blue',
  },
]
